import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Package, ArrowLeft, Search, MessageCircle, Share2, Copy, QrCode, ChevronUp, Check } from 'lucide-react';
import { getPublicLots, getPublicLotsBySlug, getDefaultCatalogSettings } from '../../services/publicLots';
import QRCode from 'react-qr-code';

export function StorePage() {
  const params = useParams();
  const businessId = params.businessId;
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lots, setLots] = useState([]);
  const [business, setBusiness] = useState(null);
  const [catalogSettings, setCatalogSettings] = useState(getDefaultCatalogSettings());
  const [searchQuery, setSearchQuery] = useState('');
  
  // Estados para compartir
  const [showShareModal, setShowShareModal] = useState(false);
  const [showQRModal, setShowQRModal] = useState(false);
  const [copied, setCopied] = useState(false);
  
  // URL para compartir
  const catalogUrl = window.location.href;

  const handleCopyLink = () => {
    navigator.clipboard.writeText(catalogUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleShareWhatsApp = () => {
    // Usar whatsapp:// en lugar de https://wa.me para abrir WhatsApp estándar
    const message = encodeURIComponent(`¡Mirá este catálogo! ${catalogUrl}`);
    const whatsappUrl = `whatsapp://send?text=${message}`;
    
    // Intentar abrir WhatsApp estándar
    window.location.href = whatsappUrl;
    
    // Fallback a wa.me si whatsapp:// no funciona (después de un breve retraso)
    setTimeout(() => {
      window.open(`https://wa.me/?text=${message}`, '_blank');
    }, 300);
    
    setShowShareModal(false);
  };

  useEffect(() => {
    async function loadData() {
      try {
        setLoading(true);
        const data = await getPublicLots(businessId);
        setLots(data.lots);
        setBusiness(data.business);
        if (data.settings) {
          setCatalogSettings(data.settings);
        }
        setLoading(false);
      } catch (err) {
        console.error('Error loading public catalog:', err);
        setError('No se pudo cargar el catálogo. Por favor, intente más tarde.');
        setLoading(false);
      }
    }
    
    loadData();
  }, [businessId]);

  // Filtrar productos por búsqueda
  const filteredLots = lots.filter(lot => 
    lot.title.toLowerCase().includes(searchQuery.toLowerCase())
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50 dark:bg-gray-900">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#00594C]"></div>
      </div>
    );
  }

  if (error || !catalogSettings.isOnline) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen p-4 bg-gray-50 dark:bg-gray-900">
        <Package className="h-16 w-16 text-gray-400 mb-4" />
        <h1 className="text-xl font-semibold text-gray-700 dark:text-gray-300 mb-2">Catálogo no disponible</h1>
        <p className="text-gray-500 text-center mb-6">{error || 'Este catálogo no está disponible actualmente.'}</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      {/* Header */}
      <div className="sticky top-0 z-10 bg-white dark:bg-gray-800 shadow-sm border-b dark:border-gray-700">
        <div className="max-w-4xl mx-auto px-4">
          <div className="flex items-center justify-between h-16">
            <button 
              onClick={() => navigate(-1)}
              className="text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200"
            >
              <ArrowLeft className="h-5 w-5" />
            </button>
            <h1 className="text-lg font-medium text-gray-800 dark:text-white">
              {business?.name || 'Catálogo Digital'}
            </h1>
            <div className="w-5"></div> {/* Spacer */}
          </div>
        </div>
      </div>

      {/* Search */}
      <div className="bg-white dark:bg-gray-800 shadow-sm border-b dark:border-gray-700 py-3">
        <div className="max-w-4xl mx-auto px-4">
          <div className="relative">
            <Search className="h-5 w-5 text-gray-400 absolute left-3 top-1/2 transform -translate-y-1/2" />
            <input
              type="text"
              placeholder="Buscar productos..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2 rounded-lg border border-gray-200 dark:border-gray-700 dark:bg-gray-700 dark:text-white focus:ring-2 focus:ring-[#00594C] focus:border-transparent"
            />
          </div>
        </div>
      </div>

      {/* Products Grid */}
      <div className="max-w-4xl mx-auto px-4 py-6">
        {filteredLots.length === 0 ? (
          <div className="text-center py-12">
            <Package className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-700 dark:text-gray-300">No hay productos disponibles</h3>
            <p className="text-gray-500 mt-2">No se encontraron productos en este catálogo.</p>
          </div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            {filteredLots.map(lot => (
              <div key={lot.id} className="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden border border-gray-100 dark:border-gray-700">
                {lot.image ? (
                  <div className="aspect-w-1 aspect-h-1 w-full overflow-hidden bg-gray-200 dark:bg-gray-700">
                    <img
                      src={lot.image}
                      alt={lot.title}
                      className="w-full h-full object-center object-cover"
                    />
                  </div>
                ) : (
                  <div className="aspect-w-1 aspect-h-1 w-full flex items-center justify-center bg-gray-100 dark:bg-gray-700">
                    <Package className="h-12 w-12 text-gray-400" />
                  </div>
                )}
                
                <div className="p-4">
                  <h3 className="font-medium text-gray-900 dark:text-white truncate">{lot.title}</h3>
                  
                  <div className="mt-2">
                    {catalogSettings.showPrices && lot.unitPrice && (
                      <p className="text-[#00594C] font-semibold">${lot.unitPrice.toFixed(2)} / {lot.unit}</p>
                    )}
                    
                    {catalogSettings.showQuantities && (
                      <p className="text-sm text-gray-500 mt-1">
                        Disponible: {lot.quantity} {lot.unit}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Botones flotantes */}
      <div className="fixed bottom-6 right-6 flex flex-col gap-3 items-end">
        {/* Botón de compartir */}
        <button
          onClick={() => setShowShareModal(true)}
          className="bg-gray-800 hover:bg-gray-700 w-14 h-14 rounded-full flex items-center justify-center shadow-lg transition-all duration-200 hover:scale-105"
          aria-label="Compartir catálogo"
        >
          <Share2 className="text-white w-6 h-6" />
        </button>
        
        {/* Botón de WhatsApp */}
        {business && (
          <a
            href={`https://wa.me/${business.phone || business.contact_phone || '5491100000000'}?text=Hola, vi tu catálogo de productos en tibodigital.com y me gustaría obtener más información.`}
            target="_blank"
            rel="noopener noreferrer"
            className="bg-[#00594C] hover:bg-[#004C41] w-14 h-14 rounded-full flex items-center justify-center shadow-lg transition-all duration-200 hover:scale-105"
            aria-label="Contactar por WhatsApp"
          >
            <MessageCircle className="text-white w-6 h-6" />
          </a>
        )}
      </div>
      
      {/* Modal de Compartir */}
      {showShareModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-end justify-center p-4">
          <div className="bg-white dark:bg-gray-800 rounded-t-2xl w-full max-w-lg">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-xl font-medium text-gray-900 dark:text-white">Compartir catálogo</h3>
                <button 
                  onClick={() => setShowShareModal(false)}
                  className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-full"
                >
                  <ChevronUp className="w-6 h-6 text-gray-500" />
                </button>
              </div>
              
              <div className="space-y-3">
                <button
                  onClick={handleCopyLink}
                  className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300 rounded-xl transition-colors"
                >
                  {copied ? (
                    <>
                      <Check className="w-5 h-5" />
                      <span>¡Copiado!</span>
                    </>
                  ) : (
                    <>
                      <Copy className="w-5 h-5" />
                      <span>Copiar link</span>
                    </>
                  )}
                </button>
                
                <button
                  onClick={() => {
                    setShowQRModal(true);
                    setShowShareModal(false);
                  }}
                  className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300 rounded-xl transition-colors"
                >
                  <QrCode className="w-5 h-5" />
                  <span>Ver QR</span>
                </button>
                
                <button
                  onClick={handleShareWhatsApp}
                  className="w-full flex items-center justify-center gap-2 px-6 py-4 bg-[#00594C] hover:bg-[#004C41] text-white rounded-xl font-medium transition-colors"
                >
                  <MessageCircle className="w-5 h-5" />
                  <span>Compartir por WhatsApp</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* QR Modal */}
      {showQRModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-sm w-full">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-medium text-gray-900 dark:text-white">Código QR del catálogo</h3>
              <button 
                onClick={() => setShowQRModal(false)}
                className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-full"
              >
                <ChevronUp className="w-6 h-6 text-gray-500" />
              </button>
            </div>
            <div className="bg-white p-4 rounded-lg flex flex-col items-center justify-center gap-4">
              <div className="w-48 h-48 bg-white p-2 rounded-lg flex items-center justify-center">
                <QRCode 
                  value={catalogUrl} 
                  size={180}
                  level="H"
                  fgColor="#000000" 
                  bgColor="#FFFFFF"
                />
              </div>
              <div className="text-center">
                <p className="text-sm text-gray-500 mb-2">Escanea este código QR para acceder al catálogo</p>
                <p className="text-xs text-gray-400 truncate max-w-[250px]">{catalogUrl}</p>
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* Toast de confirmación */}
      <div className={`fixed bottom-32 left-1/2 -translate-x-1/2 bg-gray-800 text-white px-4 py-2 rounded-lg transition-all duration-200 ${copied ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-2 pointer-events-none'}`}>
        Link copiado al portapapeles
      </div>
    </div>
  );
} 